import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DocManiaLogo, DocManiaLogoImage } from '../components/DocManiaLogo';
import { toast } from 'sonner';
import { Mail, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState(token ? 'verifying' : 'waiting');
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (token) {
      verifyEmail();
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-email?token=${token}`);
      if (response.data.success) {
        setStatus('success');
        toast.success('Email verified successfully!');
      }
    } catch (error) {
      setStatus('error');
      const msg = error.response?.data?.detail || 'Verification failed';
      toast.error(msg);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    setResendLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
      toast.success('Verification email sent. Please check your inbox.');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send verification email';
      toast.error(msg);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-brand-hero">
      <Card className="w-full max-w-md card-elevated animate-fade-in">
        <CardContent className="pt-10 pb-10 text-center space-y-6">
          <DocManiaLogoImage size={48} className="mx-auto shadow-md" />
          {/* Verifying state */}
          {status === 'verifying' && (
            <>
              <div className="h-20 w-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">Verifying Email</h1>
                <p className="text-muted-foreground">Please wait while we verify your email address...</p>
              </div>
            </>
          )}

          {/* Success state */}
          {status === 'success' && (
            <>
              <div className="h-20 w-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-primary" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">Email Verified Successfully</h1>
                <p className="text-muted-foreground">Your email has been verified. You may now log in.</p>
              </div>
              <Button 
                className="w-full glow-button font-medium" 
                onClick={() => navigate('/login')}
                data-testid="go-to-login-btn"
              >
                Continue to Login
              </Button>
            </>
          )}

          {/* Error state */}
          {status === 'error' && (
            <>
              <div className="h-20 w-20 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle className="h-10 w-10 text-destructive" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">Verification Failed</h1>
                <p className="text-muted-foreground">
                  Invalid or expired verification link. Please request a new one.
                </p>
              </div>
              <div className="space-y-3">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="resend-email-input"
                />
                <Button 
                  variant="outline"
                  className="w-full font-medium" 
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  data-testid="resend-btn"
                >
                  {resendLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </Button>
                <Link to="/login">
                  <Button variant="ghost" className="w-full">
                    Back to Login
                  </Button>
                </Link>
              </div>
            </>
          )}

          {/* Waiting state (no token, just registered) */}
          {status === 'waiting' && (
            <>
              <div className="h-20 w-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-10 w-10 text-primary" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">Check Your Email</h1>
                <p className="text-muted-foreground">
                  We've sent a verification link to your email address. Please click the link to verify your account.
                </p>
              </div>
              
              <div className="p-4 rounded-lg bg-muted/50 text-sm text-muted-foreground text-left">
                <p className="font-medium text-foreground mb-2">Didn't receive the email?</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Check your spam folder</li>
                  <li>Make sure you entered the correct email</li>
                  <li>Wait a few minutes and try again</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <Input
                  type="email"
                  placeholder="Enter your email to resend"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="resend-email-input"
                />
                <Button 
                  variant="outline"
                  className="w-full font-medium" 
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  data-testid="resend-btn"
                >
                  {resendLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </Button>
                <Link to="/login">
                  <Button variant="ghost" className="w-full" data-testid="go-to-login-btn">
                    Back to Login
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

